// components/ui/Header.tsx
import React, { useState, useRef, useEffect } from "react";
import { Menu, Bell, ChevronDown, LogOut, User, PhoneCall, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import useAuth from "../../../../hooks/features/auth/useAuth";
import { useSoftphoneContext } from "../../../../contexts/SoftphoneContext";
import { deleteUser } from "../../../../reducer/userSlice";


interface HeaderProps {
  onToggleSidebar: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSoftphoneOpen, setIsSoftphoneOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const softphoneRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  
  const { 
    deviceStatus, 
    callState, 
    activeCall,
    outboundNumber,
    setOutboundNumber,
    handleMakeCall,
    acceptCall,
    hangUpCall
  } = useSoftphoneContext();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (softphoneRef.current && !softphoneRef.current.contains(event.target as Node)) {
        setIsSoftphoneOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    dispatch(deleteUser());
    navigate("/login");
  };

  const handleNavigateToProfile = () => {
    setIsDropdownOpen(false);
    navigate(`/${user?.role}/settings`);
  };

  const initials = ((user?.first_name?.charAt(0) || "") + (user?.last_name?.charAt(0) || "")).toUpperCase() || "U";

  const getStatusInfo = () => {
    if (callState === 'in-call') {
      return {
        label: 'En communication',
        color: 'bg-red-500',
        ring: 'ring-red-500/20'
      };
    }
    if (callState === 'ringing') {
      return {
        label: 'Appel entrant',
        color: 'bg-amber-500 animate-pulse',
        ring: 'ring-amber-500/20'
      };
    }
    if (deviceStatus === 'registered') {
      return {
        label: 'Disponible',
        color: 'bg-emerald-500',
        ring: 'ring-emerald-500/20'
      };
    }
    if (deviceStatus === 'connecting') {
      return {
        label: 'Connexion...',
        color: 'bg-amber-500 animate-pulse',
        ring: 'ring-amber-500/20'
      };
    }
    if (deviceStatus === 'error') {
      return {
        label: 'Hors ligne',
        color: 'bg-red-500',
        ring: 'ring-red-500/20'
      };
    }
    return {
      label: 'Déconnecté',
      color: 'bg-slate-400',
      ring: 'ring-slate-400/20'
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <header className="bg-transparent px-8 py-4 sticky top-0 z-40 select-none">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-slate-900">Tableau de bord</h1>
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative" ref={softphoneRef}>
            <button
              onClick={() => setIsSoftphoneOpen(!isSoftphoneOpen)}
              className="relative p-2 text-slate-500 hover:text-slate-800 bg-white border border-slate-100 rounded-full shadow-sm transition-colors hover:bg-slate-50"
            >
              <PhoneCall className="w-4 h-4" />
              <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${
                deviceStatus === 'registered' ? 'bg-emerald-500' :
                deviceStatus === 'connecting' ? 'bg-amber-500 animate-pulse' :
                'bg-red-500'
              }`}></span>
            </button>

            {isSoftphoneOpen && callState === 'idle' && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-100 p-3 z-50">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Numéro de téléphone"
                    value={outboundNumber}
                    onChange={(e) => setOutboundNumber(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && outboundNumber.trim()) {
                        handleMakeCall(outboundNumber);
                        setIsSoftphoneOpen(false);
                      }
                    }}
                    className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                  <button
                    onClick={() => {
                      handleMakeCall(outboundNumber);
                      setIsSoftphoneOpen(false);
                    }}
                    disabled={deviceStatus !== 'registered' || !outboundNumber.trim()}
                    className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-all"
                  >
                    <PhoneCall size={15} />
                  </button>
                  <button
                    onClick={() => setIsSoftphoneOpen(false)}
                    className="p-1.5 text-slate-400 hover:text-slate-600"
                  >
                    <X size={16} />
                  </button>
                </div>
                {deviceStatus !== 'registered' && (
                  <p className="text-xs text-red-500 mt-2">
                    {deviceStatus === 'connecting' ? 'Connexion en cours...' : 'Softphone non disponible'}
                  </p>
                )}
              </div>
            )}

            {(callState === 'ringing' || callState === 'in-call') && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 p-3 z-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      callState === 'ringing' ? 'bg-amber-500 animate-pulse' : 'bg-red-500'
                    }`}></span>
                    <span className="text-sm font-medium text-slate-700">
                      {callState === 'ringing' ? 'Appel entrant...' : 'En communication'}
                    </span>
                  </div>
                  {callState === 'ringing' && (
                    <button
                      onClick={acceptCall}
                      className="px-3 py-1 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
                    >
                      Décrocher
                    </button>
                  )}
                  <button
                    onClick={hangUpCall}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                  >
                    Raccrocher
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 bg-white border border-slate-100 px-3 py-1.5 rounded-full shadow-sm cursor-pointer hover:bg-slate-50 transition-all text-sm font-medium text-slate-700">
            <span className={`w-2 h-2 rounded-full ${statusInfo.color} ring-4 ${statusInfo.ring}`}></span>
            <span>{statusInfo.label}</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </div>

          <button className="relative p-2 text-slate-500 hover:text-slate-800 bg-white border border-slate-100 rounded-full shadow-sm transition-colors">
            <Bell className="w-4 h-4" />
            <span className="absolute top-1 right-1 w-3 h-3 bg-rose-500 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold text-white">1</span>
          </button>

          <div className="relative" ref={dropdownRef}>
            <div
              className="flex items-center cursor-pointer"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center overflow-hidden border border-slate-200 shadow-sm">
                {user?.avatar ? (
                  <img src={user.avatar} alt="Profil" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-bold text-slate-600">{initials}</span>
                )}
              </div>
            </div>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 z-50">
                <div className="px-4 py-2 border-b border-slate-100">
                  <p className="text-xs text-slate-400">Connecté en tant que</p>
                  <p className="text-sm font-bold text-slate-800 truncate">{user?.first_name || "Utilisateur"}</p>
                </div>
                <button onClick={handleNavigateToProfile} className="w-full px-4 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" /> Mon Profil
                </button>
                <div className="border-t border-slate-100 pt-1 mt-1">
                  <button onClick={handleLogout} className="w-full px-4 py-2 text-left text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2 font-medium">
                    <LogOut className="w-4 h-4" /> Se déconnecter
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};